import React, { useState, useEffect } from "react";
import { Spinner } from "react-bootstrap";
import { Navigate, Outlet, useLocation } from "react-router-dom";

const API = process.env.REACT_APP_API_BASE_URL;

/**
 * isAuthenticated - Checks if access_token exists
 * @returns bool - True if user is authenticated, otherwise, false
 */
const isAuthenticated = async () => {
    try {
        const response = await fetch(`${API}/authenticated/`, {
            method: 'POST',
            credentials: 'include', // Include cookies
        });

        return response.status === 200;
    } catch (error) {
        console.error("Authentication check failed", error);
        return false;
    }
};

/**
 * refreshAccessToken - Refreshes an access token
 * @returns bool: True if refresh was successful, otherwise, returns false
 */
export const refreshAccessToken = async () => {
    try {
        const response = await fetch(`${API}/token/refresh/`, {
            method: 'POST',
            credentials: 'include', // Include cookies
        });

        return response.status === 200;
    } catch (error) {
        console.error("Access token refresh failed:", error);
        return false;
    }
};

/**
 * logout - Logs out user
 * @returns obj: { success: true } on success, { success: false, message: "..." } on failure
 */
export const logout = async () => {
    try {
        const response = await fetch(`${API}/logout/`, {
            method: 'POST',
            credentials: 'include', // Include cookies so access token is sent
        });

        if (response.status === 200) {
            return { success: true };
        } else {
            return { success: false, message: "Logout failed. Please try again." };
        }
    } catch (error) {
        return { success: false, message: `Logout failed: ${error.message}`}
    }
}

/**
 * ProtectedRoute - Validates admin user's auth status and renders appropriate dashboard page
 * @returns JSX component page
 */
export const ProtectedRoute = () => {
    const [isAuth, setIsAuth] = useState(null);
    const location = useLocation(); // Get current URL location

    // Auth check and refresh every 5 minutes
    useEffect(() => {
        let isMounted = true;

        const checkAndRefreshAuth = async () => {
            const authenticated = await isAuthenticated();

            // Prevent setting state after component has been unmounted
            if (isMounted) {
                if (!authenticated) {
                    const refreshed = await refreshAccessToken();
                    console.log("Refresh status:", refreshed);
                    if (refreshed) {  // Attempt refresh
                        setIsAuth(true);  // Authenticated
                    }
                    else {
                        setIsAuth(false);  // Not authenticated
                    }
                } else {
                    setIsAuth(true); // Authenticated
                }
            }
        };

        checkAndRefreshAuth();

        // Refresh access token periodically (access token lifetime is 5 minutes)
        const refreshInterval = setInterval(checkAndRefreshAuth, 270000); // 4.5 minutes

        // Clean up action by setting isMounted to false when component unmounts
        return () => {
            isMounted = false;
            clearInterval(refreshInterval);
        }
    }, []); // Run effect when isAuth changes

    if (isAuth === null) {
        return (
            <div className="mx-auto my-auto d-inline-block">
                <Spinner animation="border" variant="primary" className="d-flex align-self-center mx-auto" />
                <p className="mt-2 text-center fst-italic fs-6">Redirecting you to Admin Panel...</p>
            </div>
        )
    }

    return isAuth ? (
        <Outlet />
    ) : (
        // Pass current location to login page so user is redirected there after successful login
        <Navigate to="/admin" state={{ from: location}} replace />
    )
}
